// features/reports/components/CierreEstimado.tsx
// ============================================================================
// CIERRE ESTIMADO (bloque B3, 16-oct-2026) — sub-pestaña de Reportes › Pronóstico y presupuesto. Para Mabel.
// ¿Con cuánto cerramos el mes? = devengado a hoy + por cobrar esperado + otros ingresos (mismo mes del año anterior)
// − gastos reales − falta por pagar del presupuesto (sin la línea Nómina) − nómina que falta del horario. Y el mes siguiente.
// Dos perillas (% bajas, % cobranza; por defecto MEDIDAS) dan conservador / esperado / optimista. Nada se guarda.
// No calcula nada: lo arma el servidor (/finances/presupuesto/cierre, lib/letsmoveCierre.js); el PDF y el Excel del
// reporte traen la misma hoja con las mismas perillas. Cada cifra trae su explicación de una línea.
// Plan: forobeta-ops/letsmove/PLAN-SIGUIENTE-PASO-20261016.md §4.
// ============================================================================
import { Fragment, useEffect, useRef, useState } from 'react';
import { ChevronDown, FileSpreadsheet, FileText, Info, RotateCcw, SlidersHorizontal } from 'lucide-react';
import { getCierre, urlExportCierre, Cierre, CierreBloque } from '../api';
import { moneyEntero as money } from '../../finances/api';
import { Skeleton } from '../../../components/Skeleton';
import { readCache, writeCache } from '../../../services/localCache';

const card = 'rounded-2xl border border-line bg-surface p-4 shadow-sm';
const navBtn = 'rounded-lg border border-line bg-surface p-2 text-content-2 transition hover:bg-surface-2';
const ESC = ['conservador', 'esperado', 'optimista'] as const;
const ESC_LABEL = { conservador: 'Conservador', esperado: 'Esperado', optimista: 'Optimista' } as const;
const colorRes = (n: number) => (n >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400');
const pctTxt = (n: number | null | undefined) => (n == null ? '—' : `${Number(n).toFixed(1)} %`);
const fechaCorta = (iso: string) => iso.split('-').reverse().join('/');
const moneyS = (n: number) => (n < 0 ? '−' + money(-n) : money(n));   // negativo legible: −$1,234.00

function Perilla({ label, valor, medido, editado, onChange, ayuda }: { label: string; valor: number; medido: number; editado: boolean; onChange: (v: number | null) => void; ayuda: string }) {
  return (
    <div className="min-w-0 flex-1">
      <div className="flex items-baseline justify-between gap-2">
        <label className="text-sm font-semibold text-content">{label}</label>
        <span className="flex items-center gap-1 text-sm tabular-nums">
          <input type="number" min={0} max={100} step={0.5} value={valor}
            onChange={(e) => { const v = Number(e.target.value); if (Number.isFinite(v)) onChange(Math.min(100, Math.max(0, v))); }}
            className="w-20 rounded-lg border border-line bg-surface px-2 py-1 text-right text-content outline-none focus:border-brand-blue" aria-label={`${label} (%)`} />
          <span className="text-content-3">%</span>
        </span>
      </div>
      <input type="range" min={0} max={100} step={0.5} value={valor} onChange={(e) => onChange(Number(e.target.value))}
        className="mt-2 w-full accent-brand-blue" aria-label={label} />
      <p className="mt-1 text-xs text-content-3">
        {editado ? <>Editado · medido <b className="text-content-2">{pctTxt(medido)}</b> <button onClick={() => onChange(null)} className="ml-1 underline decoration-dotted hover:text-content">volver</button>. </> : <>Medido. </>}
        {ayuda}
      </p>
    </div>
  );
}

function Bloque({ b, titulo, total, sub }: { b: CierreBloque; titulo: string; total: string; sub: string }) {
  const [abierta, setAbierta] = useState<string | null>(null);
  const det = b.detalle;
  const detalleDe = (k: string) => {
    if (k === 'porCobrar' && det.cargos) {
      return det.cargos.lista.length ? (
        <table className="w-full text-xs">
          <thead><tr className="text-left text-content-3"><th className="py-1 pr-2 font-medium">Alumna</th><th className="py-1 pr-2 font-medium">Cargo</th><th className="py-1 pr-2 font-medium">Vence</th><th className="py-1 text-right font-medium">Saldo</th></tr></thead>
          <tbody>
            {det.cargos.lista.map((c) => (
              <tr key={c.debt_id} className="border-t border-line/60">
                <td className="py-1 pr-2 text-content-2">{c.nombre}{!c.activa && <span className="ml-1 rounded bg-surface-2 px-1 text-[10px] text-content-3">baja</span>}</td>
                <td className="py-1 pr-2 text-content-3">{c.tipo}</td>
                <td className="py-1 pr-2 tabular-nums text-content-3">{c.vence ? fechaCorta(c.vence) : '—'}</td>
                <td className="py-1 text-right tabular-nums text-content">{money(c.saldo)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <p className="text-xs text-content-3">No hay cargos abiertos este mes.</p>;
    }
    if (k === 'otros' || k === 'otrosSig') {
      return (
        <table className="w-full text-xs">
          <thead><tr className="text-left text-content-3"><th className="py-1 pr-2 font-medium">Concepto</th><th className="py-1 pr-2 text-right font-medium">{det.anioAnterior.titulo}</th>{det.otros[0]?.cobrado != null && <><th className="py-1 pr-2 text-right font-medium">Cobrado</th><th className="py-1 text-right font-medium">Falta</th></>}</tr></thead>
          <tbody>
            {det.otros.map((o) => (
              <tr key={o.k} className="border-t border-line/60">
                <td className="py-1 pr-2 text-content-2">{o.concepto}</td>
                <td className="py-1 pr-2 text-right tabular-nums text-content-3">{o.referencia == null ? 'sin dato' : money(o.referencia)}</td>
                {o.cobrado != null && <><td className="py-1 pr-2 text-right tabular-nums text-content-3">{money(o.cobrado)}</td><td className="py-1 text-right tabular-nums text-content">{money(o.falta || 0)}</td></>}
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    if (k === 'presupuestoFalta' || k === 'presupuesto') {
      const p = det.presupuesto;
      const campo = k === 'presupuesto' ? 'presupuesto' : 'faltaPorPagar';
      const filas = p.conceptos.filter((c) => c[campo] > 0.005);
      return filas.length ? (
        <table className="w-full text-xs">
          <thead><tr className="text-left text-content-3"><th className="py-1 pr-2 font-medium">Concepto</th><th className="py-1 pr-2 text-right font-medium">Presupuesto</th><th className="py-1 pr-2 text-right font-medium">Real</th><th className="py-1 text-right font-medium">{k === 'presupuesto' ? '' : 'Falta'}</th></tr></thead>
          <tbody>
            {filas.map((c) => (
              <tr key={c.categoria + c.concepto} className="border-t border-line/60">
                <td className="py-1 pr-2 text-content-2">{c.concepto}{c.calculado && <span className="ml-1 text-[10px] text-content-3">calculado</span>} <span className="text-content-3">· {c.categoria}</span></td>
                <td className="py-1 pr-2 text-right tabular-nums text-content-3">{money(c.presupuesto)}</td>
                <td className="py-1 pr-2 text-right tabular-nums text-content-3">{money(c.real)}</td>
                <td className="py-1 text-right tabular-nums text-content">{k === 'presupuesto' ? '' : money(c.faltaPorPagar)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <p className="text-xs text-content-3">{p.disponible ? 'Nada pendiente del presupuesto.' : p.aviso}</p>;
    }
    if (k === 'nominaFalta' || k === 'nomina') {
      return (
        <table className="w-full text-xs">
          <thead><tr className="text-left text-content-3"><th className="py-1 pr-2 font-medium">Semana</th><th className="py-1 pr-2 text-right font-medium">Calculada</th>{k === 'nominaFalta' && <><th className="py-1 pr-2 text-right font-medium">Registrada</th><th className="py-1 text-right font-medium">Falta</th></>}</tr></thead>
          <tbody>
            {det.nomina.semanas.map((w) => (
              <tr key={w.label} className="border-t border-line/60">
                <td className="py-1 pr-2 text-content-2">{w.label}{k === 'nominaFalta' && w.personas ? <span className="ml-1 text-content-3">· {w.personas} sin pago</span> : null}</td>
                <td className="py-1 pr-2 text-right tabular-nums text-content-3">{money(w.calculada)}</td>
                {k === 'nominaFalta' && <><td className="py-1 pr-2 text-right tabular-nums text-content-3">{money(w.registrada || 0)}</td><td className="py-1 text-right tabular-nums text-content">{money(w.falta || 0)}</td></>}
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    return null;
  };
  const conDetalle = new Set(['porCobrar', 'otros', 'presupuestoFalta', 'nominaFalta', 'presupuesto', 'nomina']);
  return (
    <div className={`${card} mt-4 p-0`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2 px-4 pt-4 pb-2">
        <h3 className="font-display text-base font-bold text-content">{titulo}</h3>
        <span className="text-xs text-content-3">{sub}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[620px] text-sm">
          <thead>
            <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-content-3">
              <th className="px-4 py-2 font-medium">Concepto</th>
              {ESC.map((k) => <th key={k} className={`px-2 py-2 text-right font-medium ${k === 'esperado' ? 'text-content-2' : ''}`}>{ESC_LABEL[k]}</th>)}
            </tr>
          </thead>
          <tbody>
            {b.lineas.map((l, i) => {
              const k = l.k === 'otros' && !det.cargos ? 'otrosSig' : l.k;
              const tiene = conDetalle.has(l.k);
              const abierto = abierta === l.k;
              return (
                <Fragment key={l.k}>
                  <tr className={`border-b border-line/60 align-top ${i % 2 ? 'bg-surface-2/40' : ''}`}>
                    <td className="px-4 py-2">
                      <button type="button" disabled={!tiene} onClick={() => setAbierta(abierto ? null : l.k)} className="flex w-full items-start gap-1 text-left disabled:cursor-default">
                        <span className={`w-3 shrink-0 font-semibold ${l.signo > 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>{l.signo > 0 ? '+' : '−'}</span>
                        <span className="min-w-0">
                          <span className="font-medium text-content">{l.concepto}</span>
                          {l.referencia && <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">referencia año anterior</span>}
                          {tiene && <ChevronDown size={14} className={`ml-1 inline text-content-3 transition ${abierto ? 'rotate-180' : ''}`} />}
                          <span className="mt-0.5 block text-xs leading-snug text-content-3">{l.explica}</span>
                        </span>
                      </button>
                    </td>
                    {ESC.map((e) => (
                      <td key={e} className={`px-2 py-2 text-right tabular-nums ${e === 'esperado' ? 'font-semibold text-content' : 'text-content-3'}`}>{moneyS(l.valores[e])}</td>
                    ))}
                  </tr>
                  {abierto && (
                    <tr className="border-b border-line/60 bg-surface-2/60">
                      <td colSpan={4} className="px-8 py-2">{detalleDe(k)}</td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-surface-2/70">
              <td className="px-4 py-3 font-semibold text-content">= {total}</td>
              {ESC.map((e) => <td key={e} className={`px-2 py-3 text-right font-display tabular-nums ${e === 'esperado' ? 'text-lg font-bold' : 'font-semibold'} ${colorRes(b.resultado[e])}`}>{moneyS(b.resultado[e])}</td>)}
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="px-4 pb-4 pt-2 text-xs text-content-3">{b.evento.explica}</p>
    </div>
  );
}

export default function CierreEstimado({ mes }: { mes: string }) {
  const [bajas, setBajas] = useState<number | null>(null);        // null = lo medido
  const [cobranza, setCobranza] = useState<number | null>(null);
  const [d, setD] = useState<Cierre | null>(() => readCache<Cierre>(`rep-cierre-${mes}`) ?? null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<{ status: number; msg: string } | null>(null);
  const [verSupuestos, setVerSupuestos] = useState(false);
  const pedido = useRef(0);

  // Al cambiar de mes, las perillas vuelven a lo medido.
  useEffect(() => { setBajas(null); setCobranza(null); }, [mes]);

  useEffect(() => {
    let vivo = true;
    const id = ++pedido.current;
    const sinPerillas = bajas == null && cobranza == null;
    if (sinPerillas) { const c = readCache<Cierre>(`rep-cierre-${mes}`); if (c) setD(c); else setD((x) => (x && x.mes === mes ? x : null)); }
    setError(null); setCargando(true);
    // Las perillas piden al servidor con una pausa corta (el cálculo vive allá: pantalla, PDF y Excel cuadran).
    const t = setTimeout(() => {
      getCierre(mes, { bajas, cobranza })
        .then((r) => { if (!vivo || id !== pedido.current) return; setD(r); if (sinPerillas) writeCache(`rep-cierre-${mes}`, r); })
        .catch((e: any) => { if (vivo) setError({ status: Number(e?.status || 0), msg: e?.message || 'No se pudo calcular el cierre estimado' }); })
        .finally(() => { if (vivo && id === pedido.current) setCargando(false); });
    }, sinPerillas ? 0 : 350);
    return () => { vivo = false; clearTimeout(t); };
  }, [mes, bajas, cobranza]);

  if (error && !d) {
    return (
      <div className={`${card} mt-4 text-sm`}>
        {error.status === 404
          ? <p className="flex items-center gap-2 text-content-2"><Info size={16} className="text-brand-blue" /> El cierre estimado se activa cuando el servidor termine de actualizarse. Vuelve a intentar más tarde.</p>
          : <p className="text-red-700 dark:text-red-300">{error.msg}</p>}
      </div>
    );
  }
  if (!d) return <Skeleton className="mt-4 h-96 w-full rounded-2xl" />;

  const su = d.supuestos;
  const perillas = { bajas: su.editado.bajas ? su.bajas : null, cobranza: su.editado.cobranza ? su.cobranza : null };
  const editadas = su.editado.bajas || su.editado.cobranza;

  return (
    <div className={cargando ? 'opacity-70 transition' : 'transition'}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-content-3">
          <b className="text-content-2">Cierre estimado de {d.titulo.toLowerCase()}</b>
          {d.enCurso ? ` · con datos al ${fechaCorta(d.hoy)}` : d.pasado ? ' · mes cerrado: lo que falta es solo lo pendiente' : ''}. Nada se guarda: es un cálculo con los datos de hoy.
        </p>
        {(d.cierre || d.siguiente) && (
          <div className="flex items-center gap-2">
            <a href={urlExportCierre(mes, 'xlsx', perillas)} download title="Excel del reporte con la hoja «Cierre estimado» (con estas perillas)" className={`${navBtn} flex items-center gap-1.5 text-sm`}>
              <FileSpreadsheet size={16} /> <span className="hidden sm:inline">Excel</span>
            </a>
            <a href={urlExportCierre(mes, 'pdf', perillas)} target="_blank" rel="noopener" title="PDF del reporte con la hoja «Cierre estimado» (con estas perillas)" className={`${navBtn} flex items-center gap-1.5 text-sm`}>
              <FileText size={16} /> <span className="hidden sm:inline">PDF</span>
            </a>
          </div>
        )}
      </div>

      {d.aviso && <p className={`${card} flex items-center gap-2 text-sm text-content-2`}><Info size={16} className="shrink-0 text-brand-blue" /> {d.aviso}</p>}

      {/* Perillas */}
      {(d.cierre || d.siguiente) && (
        <div className={card}>
          <div className="mb-3 flex items-center justify-between gap-2">
            <h3 className="flex items-center gap-2 font-display text-base font-bold text-content"><SlidersHorizontal size={16} className="text-brand-blue" /> Supuestos</h3>
            {editadas && <button onClick={() => { setBajas(null); setCobranza(null); }} className="flex items-center gap-1 text-xs text-content-3 hover:text-content"><RotateCcw size={13} /> Volver a lo medido</button>}
          </div>
          <div className="flex flex-col gap-5 sm:flex-row sm:gap-8">
            <Perilla label="% de bajas del mes" valor={bajas ?? su.bajas} medido={su.bajasMedido} editado={su.editado.bajas} onChange={setBajas}
              ayuda={`${su.medicion.bajas.explica} Año anterior (${su.medicion.bajas.anioAnterior.titulo}): ${pctTxt(su.medicion.bajas.anioAnterior.pct)}.`} />
            <Perilla label="% de cobranza" valor={cobranza ?? su.cobranza} medido={su.cobranzaMedido} editado={su.editado.cobranza} onChange={setCobranza}
              ayuda={su.medicion.cobranza.explica} />
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
            {ESC.map((e) => (
              <div key={e} className={`rounded-xl px-2 py-2 ${e === 'esperado' ? 'bg-accent/10 ring-1 ring-accent/30' : 'bg-surface-2'}`}>
                <div className="font-semibold text-content-2">{ESC_LABEL[e]}</div>
                <div className="mt-0.5 tabular-nums text-content-3">bajas {pctTxt(su.escenarios[e].bajas)} · cobranza {pctTxt(su.escenarios[e].cobranza)}</div>
              </div>
            ))}
          </div>
          <button onClick={() => setVerSupuestos(!verSupuestos)} className="mt-3 flex items-center gap-1 text-xs text-content-3 hover:text-content">
            Cómo se midieron <ChevronDown size={14} className={`transition ${verSupuestos ? 'rotate-180' : ''}`} />
          </button>
          {verSupuestos && (
            <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-content-3">
              <li>{su.explica}</li>
              <li>{su.medicion.cobranza.bajasExplica}</li>
              <li>{su.medicion.cobranza.aTiempoExplica}</li>
              <li>{su.notaBajasMesEnCurso}</li>
            </ul>
          )}
        </div>
      )}

      {/* KPI del cierre */}
      {d.cierre && (
        <div className="mt-4 grid grid-cols-3 gap-2 sm:gap-3">
          {ESC.map((e) => (
            <div key={e} className={`${card} ${e === 'esperado' ? 'ring-1 ring-accent/40' : ''} p-3 sm:p-4`}>
              <div className="text-[11px] font-medium uppercase tracking-wide text-content-3">{ESC_LABEL[e]}</div>
              <div className={`mt-1 font-display text-base font-bold tabular-nums sm:text-xl ${colorRes(d.cierre!.resultado[e])}`}>{moneyS(d.cierre!.resultado[e])}</div>
              <div className="mt-0.5 text-[11px] text-content-3">cierre de {d.cierre!.titulo.split(' ')[0].toLowerCase()}</div>
            </div>
          ))}
        </div>
      )}

      {d.cierre && <Bloque b={d.cierre} titulo={`Cierre estimado de ${d.cierre.titulo.toLowerCase()}`} total={`Cierre estimado de ${d.cierre.titulo.toLowerCase()}`}
        sub={d.enCurso ? 'lo que ya pasó + lo que falta' : 'mes cerrado'} />}
      {d.siguiente && <Bloque b={d.siguiente} titulo={`Mes siguiente: ${d.siguiente.titulo.toLowerCase()}`} total={`Resultado esperado de ${d.siguiente.titulo.toLowerCase()}`}
        sub="pronóstico con los planes, el horario y el presupuesto de hoy" />}
      {!d.siguiente && d.cierre && <p className="mt-3 text-xs text-content-3">El pronóstico del mes siguiente sale cuando ves el mes en curso.</p>}

      <ul className="mt-4 space-y-1 text-xs text-content-3">
        {d.notas.map((n) => <li key={n}>{n}</li>)}
        <li>Generado el {d.generado}.</li>
      </ul>
    </div>
  );
}
